import { Form, InputNumber, Radio, Typography, Switch, Col, Row, Modal } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import generatePicker from 'antd/es/date-picker/generatePicker';
import utc from 'dayjs/plugin/utc';
import isoWeek from 'dayjs/plugin/isoWeek';
import { MetingenInputDto, MetingInputDto } from '@/lib/types';
import updateLocale from 'dayjs/plugin/updateLocale';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import { useTranslation } from 'react-i18next';
import styles from '../../../page.module.scss';
import { Conditional } from '@/lib/components/core/conditional/conditional';

dayjs.extend(utc);
dayjs.extend(isoWeek);

dayjs.extend(updateLocale);
dayjs.extend(isoWeeksInYear);

const datePickerConfig = dayjsGenerateConfig;
const DatePickerComponent = generatePicker<Dayjs>(datePickerConfig);

interface Properties {
  teeltwijze: number;
}

export const MetingenV2: FC<Properties> = ({ teeltwijze }) => {
  const { Title } = Typography;
  const { t } = useTranslation('input');
  const form = Form.useFormInstance();
  const registratiejaar = form.getFieldValue('registratiejaar');

  const [metingenList, setMetingenList] = useState<MetingenInputDto[]>(form.getFieldValue('metingen'));
  const [initialized, setInitialized] = useState(false);
  const currentMetingenIndex = metingenList ? metingenList.findIndex(m => m.betrekkingTotTeeltwijze === teeltwijze) : -1;

  const opgeslagenAantal = Form.useWatch(['metingen', currentMetingenIndex, 'aantalMetingenPerJaar'], { form, preserve: true });

  let metingOptions: { key: string; label: string; value: number }[];
  switch (teeltwijze) {
    case 0:
      metingOptions = [{ key: '0', label: '4', value: 4 }];
      break;
    case 1:
      metingOptions = [{ key: '1', label: '13', value: 13 }];
      break;
    default:
      metingOptions = [{ key: '0', label: '0', value: 0 }];
  }

  const aantalMetingenPerJaar = metingOptions.some(option => option.value === opgeslagenAantal)
    ? opgeslagenAantal
    : Math.max(...metingOptions.map(option => option.value));

  useEffect(() => {
    if (currentMetingenIndex < 0) return;
    if (form.getFieldValue(['metingen', currentMetingenIndex, 'aantalMetingenPerJaar']) !== aantalMetingenPerJaar) {
      form.setFieldValue(['metingen', currentMetingenIndex, 'aantalMetingenPerJaar'], aantalMetingenPerJaar);
    }
  }, [form, currentMetingenIndex, aantalMetingenPerJaar, opgeslagenAantal]);

  useEffect(() => {
    const existingMetingen: MetingenInputDto[] = form.getFieldValue('metingen') || [];
    const hasMetingen = existingMetingen.some((m: MetingenInputDto) => m.betrekkingTotTeeltwijze === teeltwijze);

    if (hasMetingen) {
      const idx = existingMetingen.findIndex(m => m.betrekkingTotTeeltwijze === teeltwijze);
      if (idx === -1) return;
      const item = existingMetingen[idx];
      const restorePeriods = (rows: MetingInputDto[] = []) => {
        const restored: MetingInputDto[] = [];
        for (const row of rows) {
          if (row?.periode == null) continue;
          restored[row.periode - 1] = row.geenMeting ? { ...row, datum: undefined, gehalte: undefined } : row;
        }
        return restored;
      };

      const normalizedMetingen = {
        ...item,
        nH4Metingen: restorePeriods(item.nH4Metingen),
        nO3Metingen: restorePeriods(item.nO3Metingen),
        pMetingen: restorePeriods(item.pMetingen),
        naMetingen: restorePeriods(item.naMetingen),
      };
      form.setFieldValue(['metingen', idx], normalizedMetingen);
      setMetingenList(existingMetingen.map((metingen, index) => (index === idx ? normalizedMetingen : metingen)));
    } else {
      const newMetingenInputDto: MetingenInputDto = {
        betrekkingTotTeeltwijze: teeltwijze,
        aantalMetingenPerJaar: 0,
        nH4Metingen: [],
        nO3Metingen: [],
        pMetingen: [],
        naMetingen: [],
        isBerekend: false,
      };

      const updatedMetingen = [...existingMetingen, newMetingenInputDto];

      form.setFieldsValue({ metingen: updatedMetingen });

      setMetingenList(updatedMetingen);
    }
    setInitialized(true);
  }, [teeltwijze, form]);

  const startOfISOYear = useMemo(
    () => (registratiejaar == null ? dayjs() : dayjs.utc(new Date(Date.UTC(registratiejaar, 0, 4))).startOf('isoWeek')),
    [registratiejaar]
  );
  const endOfISOYear = useMemo(
    () =>
      dayjs
        .utc(new Date(Date.UTC(registratiejaar ?? 0, 11, 28)))
        .endOf('isoWeek')
        .subtract(1, 'day'),
    [registratiejaar]
  );

  const disableOutOfISOYear = (currentDate: Dayjs) => {
    return currentDate.isBefore(startOfISOYear) || currentDate.isAfter(endOfISOYear);
  };

  const toggleGeenMetingRow = (idx: number, checked: boolean) => {
    if (checked) {
      form.setFieldValue(['metingen', currentMetingenIndex, 'nH4Metingen', idx, 'gehalte'], undefined);
      form.setFieldValue(['metingen', currentMetingenIndex, 'nH4Metingen', idx, 'datum'], undefined);
      form.setFieldValue(['metingen', currentMetingenIndex, 'nH4Metingen', idx, 'geenMeting'], true);
      form.setFieldValue(['metingen', currentMetingenIndex, 'nO3Metingen', idx, 'gehalte'], undefined);
      form.setFieldValue(['metingen', currentMetingenIndex, 'nO3Metingen', idx, 'geenMeting'], true);
      form.setFieldValue(['metingen', currentMetingenIndex, 'naMetingen', idx, 'gehalte'], undefined);
      form.setFieldValue(['metingen', currentMetingenIndex, 'naMetingen', idx, 'geenMeting'], true);
      form.setFieldValue(['metingen', currentMetingenIndex, 'naMetingen', idx, 'geenNatrium'], false);
    } else {
      form.setFieldValue(['metingen', currentMetingenIndex, 'nH4Metingen', idx, 'geenMeting'], false);
      form.setFieldValue(['metingen', currentMetingenIndex, 'nO3Metingen', idx, 'geenMeting'], false);
      form.setFieldValue(['metingen', currentMetingenIndex, 'naMetingen', idx, 'geenMeting'], false);
    }
  };

  const data = useMemo(() => Array.from({ length: aantalMetingenPerJaar || 0 }, (_, i) => ({ key: i, periode: i + 1 })), [aantalMetingenPerJaar]);

  const listCols = { periode: 2, datum: 3, nh4: 3, no3: 3, p: 3, na: 3, geenMeting: 2, geenNatrium: 2 };

  return (
    <>
      <Title level={5}>Metingen</Title>
      <Typography style={{ marginBottom: 10 }}>
        <Conditional condition={teeltwijze === 0}>{t('form.emissionReport.metingen.subtext.grondteelt')}</Conditional>
        <Conditional condition={teeltwijze === 1}>{t('form.emissionReport.metingen.subtext.substraatteelt')}</Conditional>
      </Typography>
      <Form.Item name={['metingen', currentMetingenIndex, 'betrekkingTotTeeltwijze']} initialValue={teeltwijze} noStyle>
        <input type="hidden" />
      </Form.Item>
      <Form.Item
        label="Aantal metingen per jaar"
        name={['metingen', currentMetingenIndex, 'aantalMetingenPerJaar']}
        rules={[{ required: true, message: 'Selecteer het aantal metingen per jaar' }]}>
        <Radio.Group options={metingOptions} optionType="button" buttonStyle="solid" />
      </Form.Item>
      <Conditional condition={initialized && aantalMetingenPerJaar > 0}>
        <>
          <Row style={{ fontWeight: 'bold', marginBottom: 4 }}>
            <Col span={listCols.periode} style={{ minWidth: 80 }}>
              {t('form.emissionReport.metingen.headers.period')}
            </Col>
            <Col span={listCols.datum} style={{ minWidth: 100 }}>
              {t('form.emissionReport.metingen.headers.date')}
            </Col>
            <Col span={listCols.nh4} style={{ minWidth: 100 }}>
              {t('form.emissionReport.metingen.headers.nh4')}
            </Col>
            <Col span={listCols.no3} style={{ minWidth: 100 }}>
              {t('form.emissionReport.metingen.headers.no3')}
            </Col>
            <Conditional condition={teeltwijze === 0}>
              <Col span={listCols.p} style={{ minWidth: 100 }}>
                {t('form.emissionReport.metingen.headers.p')}
              </Col>
            </Conditional>
            <Conditional condition={teeltwijze === 1}>
              <>
                <Col span={listCols.na} style={{ minWidth: 100 }}>
                  {t('form.emissionReport.metingen.headers.na')}
                </Col>
                <Col span={listCols.geenMeting}>{t('form.emissionReport.metingen.headers.geenMeting')}</Col>
                <Conditional condition={registratiejaar === 2026}>
                  <Col span={listCols.geenNatrium}>{t('form.emissionReport.metingen.headers.geenNatrium')}</Col>
                </Conditional>
              </>
            </Conditional>
          </Row>
          {data.map(({ key, periode }, index) => (
            <Form.Item key={key} noStyle shouldUpdate={(prev, curr) => prev !== curr}>
              {() => {
                const disabledRow = form.getFieldValue(['metingen', currentMetingenIndex, 'nH4Metingen', index, 'geenMeting']) === true;
                const disabledNatrium = form.getFieldValue(['metingen', currentMetingenIndex, 'naMetingen', index, 'geenNatrium']) === true;
                return (
                  <Row className={styles.rowBorder} style={{ padding: 4, opacity: disabledRow ? 0.95 : 1 }}>
                    <Col span={listCols.periode} style={{ minWidth: 80 }}>
                      <Form.Item name={['metingen', currentMetingenIndex, 'nH4Metingen', index, 'periode']} initialValue={periode} style={{ marginBottom: 0 }}>
                        <span>{periode}</span>
                      </Form.Item>
                      <Form.Item name={['metingen', currentMetingenIndex, 'nO3Metingen', index, 'periode']} initialValue={periode} hidden>
                        <InputNumber />
                      </Form.Item>
                      <Form.Item name={['metingen', currentMetingenIndex, 'pMetingen', index, 'periode']} initialValue={periode} hidden>
                        <InputNumber />
                      </Form.Item>
                      <Form.Item name={['metingen', currentMetingenIndex, 'naMetingen', index, 'periode']} initialValue={periode} hidden>
                        <InputNumber />
                      </Form.Item>
                      <Form.Item
                        name={['metingen', currentMetingenIndex, 'nH4Metingen', index, 'geenMeting']}
                        initialValue={form.getFieldValue(['metingen', currentMetingenIndex, 'nH4Metingen', index, 'geenMeting']) ?? false}
                        hidden>
                        <InputNumber />
                      </Form.Item>
                      <Form.Item
                        name={['metingen', currentMetingenIndex, 'nO3Metingen', index, 'geenMeting']}
                        initialValue={form.getFieldValue(['metingen', currentMetingenIndex, 'nO3Metingen', index, 'geenMeting']) ?? false}
                        hidden>
                        <InputNumber />
                      </Form.Item>
                      <Form.Item
                        name={['metingen', currentMetingenIndex, 'naMetingen', index, 'geenMeting']}
                        initialValue={form.getFieldValue(['metingen', currentMetingenIndex, 'naMetingen', index, 'geenMeting']) ?? false}
                        hidden>
                        <InputNumber />
                      </Form.Item>
                      <Form.Item
                        name={['metingen', currentMetingenIndex, 'naMetingen', index, 'geenNatrium']}
                        initialValue={form.getFieldValue(['metingen', currentMetingenIndex, 'naMetingen', index, 'geenNatrium']) ?? false}
                        hidden>
                        <InputNumber />
                      </Form.Item>
                    </Col>
                    <Col span={listCols.datum} style={{ minWidth: 100 }}>
                      <Form.Item
                        name={['metingen', currentMetingenIndex, 'nH4Metingen', index, 'datum']}
                        getValueProps={value => ({ value: value ? dayjs(value) : undefined })}
                        getValueFromEvent={value => value?.toISOString()}>
                        <DatePickerComponent
                          format={'DD-MM-YYYY'}
                          showWeek
                          defaultPickerValue={registratiejaar ? dayjs.utc(new Date(Date.UTC(registratiejaar, 0, 1))) : undefined}
                          disabledDate={disableOutOfISOYear}
                          style={{ width: '90%' }}
                          disabled={disabledRow}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={listCols.nh4} style={{ minWidth: 100 }}>
                      <Form.Item name={['metingen', currentMetingenIndex, 'nH4Metingen', index, 'gehalte']}>
                        <InputNumber min={0} decimalSeparator="," precision={2} step={0.01} max={99999999.9} style={{ width: '90%' }} disabled={disabledRow} />
                      </Form.Item>
                    </Col>
                    <Col span={listCols.no3} style={{ minWidth: 100 }}>
                      <Form.Item name={['metingen', currentMetingenIndex, 'nO3Metingen', index, 'gehalte']}>
                        <InputNumber min={0} decimalSeparator="," precision={2} step={0.01} max={99999999.9} style={{ width: '90%' }} disabled={disabledRow} />
                      </Form.Item>
                    </Col>
                    <Conditional condition={teeltwijze === 0}>
                      <Col span={listCols.p} style={{ minWidth: 100 }}>
                        <Form.Item name={['metingen', currentMetingenIndex, 'pMetingen', index, 'gehalte']}>
                          <InputNumber
                            min={0}
                            decimalSeparator=","
                            precision={2}
                            step={0.01}
                            max={99999999.9}
                            style={{ width: '80%' }}
                            disabled={disabledRow}
                          />
                        </Form.Item>
                      </Col>
                    </Conditional>
                    <Conditional condition={teeltwijze === 1}>
                      <>
                        <Col span={listCols.na} style={{ minWidth: 100 }}>
                          <Form.Item name={['metingen', currentMetingenIndex, 'naMetingen', index, 'gehalte']}>
                            <InputNumber
                              min={0}
                              decimalSeparator=","
                              precision={2}
                              step={0.01}
                              max={99999999.9}
                              style={{ width: '80%' }}
                              disabled={disabledRow || disabledNatrium}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={listCols.geenMeting}>
                          <Switch
                            checked={disabledRow}
                            onChange={checked => {
                              if (checked && (registratiejaar ?? 0) >= 2026) {
                                Modal.error({
                                  title: 'Pas op',
                                  content: t('form.emissionReport.metingen.modal.text'),
                                });
                              }
                              toggleGeenMetingRow(index, checked);
                            }}
                          />
                        </Col>
                        <Conditional condition={registratiejaar === 2026}>
                          <Col span={listCols.geenNatrium}>
                            <Switch
                              checked={disabledNatrium}
                              onChange={checked => {
                                form.setFieldValue(['metingen', currentMetingenIndex, 'naMetingen', index, 'geenNatrium'], checked);
                                if (checked) {
                                  form.setFieldValue(['metingen', currentMetingenIndex, 'naMetingen', index, 'gehalte'], undefined);
                                }
                              }}
                            />
                          </Col>
                        </Conditional>
                      </>
                    </Conditional>
                  </Row>
                );
              }}
            </Form.Item>
          ))}
        </>
      </Conditional>
    </>
  );
};
